/**
 * Batch Fetcher Utility
 * 
 * Provides efficient batch fetching for Firestore entities.
 * Handles Firestore's `in` query limit (30 items) by chunking arrays.
 * 
 * @see docs/cms-improvements/PHASE_1_BATCH_RESOLUTION.md
 */

import {
  collection,
  query,
  where,
  getDocs,
  documentId,
  CollectionReference,
  DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Maximum number of items allowed in a Firestore `in` query.
 * Firestore limits `in`, `not-in`, and `array-contains-any` to 30 values.
 */
export const FIRESTORE_IN_QUERY_LIMIT = 30;

/**
 * Splits an array into chunks of specified size.
 * Used to work around Firestore's `in` query limit.
 */
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Options for batch fetching
 */
export interface BatchFetchOptions<T> {
  /** Function to transform raw Firestore document data to entity type */
  transform?: (id: string, data: DocumentData) => T;
  /** Filter function to exclude certain entities from results */
  filter?: (entity: T) => boolean;
}

/**
 * Fetches multiple documents by ID using Firestore's `in` query.
 * More efficient than individual getDoc calls for large batches.
 * 
 * @param collectionRef - Firestore collection reference
 * @param ids - Array of document IDs to fetch
 * @param options - Optional transform and filter functions
 * @returns Map of id -> entity for O(1) lookup
 * 
 * @example
 * ```typescript
 * const users = await batchFetchByIds(
 *   collection(db, 'users'),
 *   ['user1', 'user2', 'user3'],
 *   { transform: (id, data) => ({ id, ...data }) }
 * );
 * ```
 */
export async function batchFetchByIds<T>(
  collectionRef: CollectionReference<DocumentData>,
  ids: string[],
  options: BatchFetchOptions<T> = {}
): Promise<Map<string, T>> {
  const resultMap = new Map<string, T>();

  if (!db || ids.length === 0) {
    return resultMap;
  }

  // Deduplicate IDs
  const uniqueIds = [...new Set(ids)];

  // Chunk the IDs to respect Firestore's limit
  const chunks = chunkArray(uniqueIds, FIRESTORE_IN_QUERY_LIMIT);

  // Fetch all chunks in parallel
  const chunkPromises = chunks.map(async (chunkIds) => {
    try {
      const q = query(collectionRef, where(documentId(), 'in', chunkIds));
      const snapshot = await getDocs(q);
      return snapshot.docs;
    } catch (error) {
      console.error('Error fetching batch chunk:', error);
      return [];
    }
  });

  const chunkResults = await Promise.all(chunkPromises);

  // Flatten results and transform
  for (const docs of chunkResults) {
    for (const doc of docs) {
      const data = doc.data();
      const entity = options.transform
        ? options.transform(doc.id, data)
        : ({ id: doc.id, ...data } as unknown as T);

      // Apply filter if provided
      if (!options.filter || options.filter(entity)) {
        resultMap.set(doc.id, entity);
      }
    }
  }

  return resultMap;
}

/**
 * Generic batch fetcher with caching support.
 * Wraps an existing single-item fetcher to provide batch fetching with cache.
 * 
 * This is useful when you already have a single-item fetcher and want to add
 * batch support without changing the underlying implementation.
 * 
 * @param ids - Array of IDs to fetch
 * @param fetcher - Function to fetch a single item by ID
 * @param options - Caching and filtering options
 * @returns Map of id -> entity
 */
export async function batchFetchWithFetcher<T>(
  ids: string[],
  fetcher: (id: string) => Promise<T | null>,
  options: {
    cache?: Map<string, { entity: T; timestamp: number }>;
    cacheDuration?: number;
    filter?: (entity: T) => boolean;
  } = {}
): Promise<Map<string, T>> {
  const resultMap = new Map<string, T>();
  const now = Date.now();
  const { cache, cacheDuration = 5 * 60 * 1000, filter } = options;

  if (ids.length === 0) {
    return resultMap;
  }

  // Deduplicate IDs
  const uniqueIds = [...new Set(ids)];
  const uncachedIds: string[] = [];

  // Check cache first
  for (const id of uniqueIds) {
    if (cache) {
      const cached = cache.get(id);
      if (cached && (now - cached.timestamp) < cacheDuration) {
        if (!filter || filter(cached.entity)) {
          resultMap.set(id, cached.entity);
        }
        continue;
      }
    }
    uncachedIds.push(id);
  }

  // Fetch uncached items in parallel
  if (uncachedIds.length > 0) {
    const fetchPromises = uncachedIds.map(async (id) => {
      try {
        const entity = await fetcher(id);
        return { id, entity };
      } catch {
        return { id, entity: null };
      }
    });

    const results = await Promise.all(fetchPromises);

    for (const { id, entity } of results) {
      if (entity !== null) {
        // Update cache
        if (cache) {
          cache.set(id, { entity, timestamp: now });
        }

        // Apply filter
        if (!filter || filter(entity)) {
          resultMap.set(id, entity);
        }
      }
    }
  }

  return resultMap;
}

/**
 * Creates a memoized batch fetcher with LRU-style cache.
 * Returns a function that can be called repeatedly with different IDs.
 * 
 * @param fetcher - Function to fetch a single item by ID
 * @param options - Cache configuration
 * @returns Batch fetch function with built-in caching
 */
export function createBatchFetcher<T>(
  fetcher: (id: string) => Promise<T | null>,
  options: {
    cacheDuration?: number;
    maxCacheSize?: number;
  } = {}
): (ids: string[]) => Promise<Map<string, T>> {
  const { cacheDuration = 5 * 60 * 1000, maxCacheSize = 500 } = options;
  const cache = new Map<string, { entity: T; timestamp: number }>();

  // Simple LRU cleanup
  const cleanupCache = () => {
    if (cache.size > maxCacheSize) {
      const entriesToRemove = cache.size - maxCacheSize;
      const entries = Array.from(cache.entries());
      // Remove oldest entries
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      for (let i = 0; i < entriesToRemove; i++) {
        cache.delete(entries[i][0]);
      }
    }
  };

  return async (ids: string[]): Promise<Map<string, T>> => {
    cleanupCache();
    return batchFetchWithFetcher(ids, fetcher, { cache, cacheDuration });
  };
}


